import React, { useEffect, useState, useMemo, useRef } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import {
  ArrowLeft,
  Flame,
  TrendingUp,
  TrendingDown,
  Minus,
  Radar,
  Eye,
  Calendar,
  ChevronDown,
  Loader2,
  AlertCircle,
  RefreshCw,
  DollarSign,
  Check
} from 'lucide-react'
import { useToast } from '@/contexts/ToastContext'
import useScoutStore from '@/stores/scoutStore'
import useGlobalCustomerStore from '@/stores/globalCustomerStore'
import ScoutPostCard from './ScoutPostCard'
import KPICard from '@/components/ui/KPICard'

const TrendIcon: React.FC<{ trend: string }> = ({ trend }) => {
  switch (trend) {
    case 'rising':
      return <TrendingUp className="h-3.5 w-3.5 text-emerald-600" />
    case 'declining':
      return <TrendingDown className="h-3.5 w-3.5 text-red-600" />
    default:
      return <Minus className="h-3.5 w-3.5 text-gray-400" />
  }
}

const trendLabel: Record<string, string> = {
  rising: 'Rising',
  declining: 'Declining',
  stable: 'Stable'
}

const heatBadgeStyles: Record<string, string> = {
  hot: 'bg-red-100 text-red-700',
  warm: 'bg-amber-100 text-amber-700',
  cold: 'bg-blue-100 text-blue-700'
}

const formatEMV = (value: number | string): string => {
  const n = Number(value)
  if (n >= 1000000) return `$${(n / 1000000).toFixed(1)}M`
  if (n >= 1000) return `$${(n / 1000).toFixed(1)}K`
  return `$${n.toFixed(2)}`
}

const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return '-'
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  })
}

const BrandDetailPage: React.FC = () => {
  const { brandId } = useParams<{ brandId: string }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { showError } = useToast()
  const { selectedCustomer } = useGlobalCustomerStore()
  const {
    selectedBrand,
    brandPosts,
    postsPagination,
    postsSummary,
    detailLoading,
    postsLoading,
    loadBrandDetail,
    loadBrandPosts
  } = useScoutStore()

  const [postsPage, setPostsPage] = useState(1)
  const [detailError, setDetailError] = useState<string | null>(null)
  const [selectedTypes, setSelectedTypes] = useState<string[]>([])
  const [selectedPlacements, setSelectedPlacements] = useState<string[]>([])
  const [selectedCategories, setSelectedCategories] = useState<string[]>([])
  const [typeDropdownOpen, setTypeDropdownOpen] = useState(false)
  const [placementDropdownOpen, setPlacementDropdownOpen] = useState(false)
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false)
  const typeDropdownRef = useRef<HTMLDivElement>(null)
  const placementDropdownRef = useRef<HTMLDivElement>(null)
  const categoryDropdownRef = useRef<HTMLDivElement>(null)
  const customerId = selectedCustomer?.id ? String(selectedCustomer.id) : ''

  const filterAccountIds = useMemo(
    () => searchParams.get('accountIds')?.split(',').filter(Boolean),
    [searchParams]
  )
  const filterPeriod = useMemo(() => searchParams.get('period') || undefined, [searchParams])
  const filterCustomStart = useMemo(() => searchParams.get('customStartDate') || undefined, [searchParams])
  const filterCustomEnd = useMemo(() => searchParams.get('customEndDate') || undefined, [searchParams])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (typeDropdownRef.current && !typeDropdownRef.current.contains(e.target as Node)) {
        setTypeDropdownOpen(false)
      }
      if (placementDropdownRef.current && !placementDropdownRef.current.contains(e.target as Node)) {
        setPlacementDropdownOpen(false)
      }
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(e.target as Node)) {
        setCategoryDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Brand detail — only re-fetches when brand or customer changes
  useEffect(() => {
    if (brandId && customerId) {
      setDetailError(null)
      loadBrandDetail(brandId, customerId).catch(() => {
        setDetailError('Unable to load brand details')
        showError('Unable to load brand')
      })
    }
  }, [brandId, customerId, loadBrandDetail, showError])

  // Posts — re-fetches on brand/customer change, page change, or any filter change
  useEffect(() => {
    if (brandId && customerId) {
      loadBrandPosts(
        brandId, customerId, postsPage, 20,
        filterAccountIds,
        filterPeriod, filterCustomStart, filterCustomEnd,
        selectedTypes.length > 0 ? selectedTypes : undefined,
        selectedPlacements.length > 0 ? selectedPlacements : undefined,
        selectedCategories.length > 0 ? selectedCategories : undefined
      ).catch(() => {
        showError('Unable to load posts')
      })
    }
  }, [brandId, customerId, postsPage, filterAccountIds, filterPeriod, filterCustomStart, filterCustomEnd, selectedTypes, selectedPlacements, selectedCategories, loadBrandPosts, showError])

  const handleRetry = () => {
    if (brandId && customerId) {
      setDetailError(null)
      loadBrandDetail(brandId, customerId).catch(() => {
        setDetailError('Unable to load brand details')
      })
      loadBrandPosts(brandId, customerId, 1, 20, filterAccountIds, filterPeriod, filterCustomStart, filterCustomEnd, selectedTypes.length > 0 ? selectedTypes : undefined, selectedPlacements.length > 0 ? selectedPlacements : undefined, selectedCategories.length > 0 ? selectedCategories : undefined).catch(() => {})
    }
  }

  if (detailLoading) {
    return (
      <div className="min-h-screen bg-gray-50/50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    )
  }

  if (detailError || !selectedBrand) {
    return (
      <div className="min-h-screen bg-gray-50/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 mb-6 transition-colors"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to Brand Scout
          </button>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 flex flex-col items-center justify-center py-12 text-center">
            <div className="h-12 w-12 rounded-full bg-red-100 flex items-center justify-center mb-4">
              <AlertCircle className="h-6 w-6 text-red-600" />
            </div>
            <h3 className="text-sm font-semibold text-gray-900 mb-1">
              {detailError ? 'Unable to load brand' : 'Brand not found'}
            </h3>
            <p className="text-xs text-gray-500 mb-4 max-w-sm">{detailError || 'The requested brand could not be found.'}</p>
            <button
              onClick={detailError ? handleRetry : () => navigate(-1)}
              className="inline-flex items-center px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              {detailError ? (
                <>
                  <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                  Try Again
                </>
              ) : (
                'Back to Brand Scout'
              )}
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 mb-6 transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to Brand Scout
        </button>

        {/* Brand Header */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                <Radar className="h-5 w-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">{selectedBrand.brand_name}</h1>
                {selectedBrand.category && (
                  <p className="text-sm text-gray-600">{selectedBrand.category}</p>
                )}
              </div>
            </div>

            <div className="flex items-center gap-3">
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${heatBadgeStyles[selectedBrand.heat_category] || heatBadgeStyles.cold}`}>
                <Flame className="h-3 w-3 mr-1" />
                {Number(selectedBrand.heat_score).toFixed(1)}
              </span>
              <span className="flex items-center gap-1 text-xs text-gray-600">
                <TrendIcon trend={selectedBrand.heat_trend} />
                {trendLabel[selectedBrand.heat_trend] || 'Stable'}
              </span>
            </div>
          </div>

        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-5 gap-3 mb-6">
          <KPICard
            label="Detections"
            value={postsSummary?.total_detections ?? 0}
            icon={Eye}
            colorScheme="blue"
            loading={postsLoading}
          />
          <KPICard
            label="Total EMV"
            value={formatEMV(postsSummary?.total_emv ?? 0)}
            icon={DollarSign}
            colorScheme="emerald"
            loading={postsLoading}
          />
          <KPICard
            label="Avg EMV"
            value={formatEMV(
              postsSummary && postsSummary.total_detections > 0
                ? postsSummary.total_emv / postsSummary.total_detections
                : 0
            )}
            icon={DollarSign}
            colorScheme="amber"
            loading={postsLoading}
          />
          <KPICard
            label="First Seen"
            value={formatDate(postsSummary?.first_seen_at ?? null)}
            icon={Calendar}
            colorScheme="indigo"
            loading={postsLoading}
          />
          <KPICard
            label="Last Seen"
            value={formatDate(postsSummary?.last_seen_at ?? null)}
            icon={Calendar}
            colorScheme="purple"
            loading={postsLoading}
          />
        </div>

        {/* Posts Grid */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-gray-700">
              Posts with {selectedBrand.brand_name} ({postsPagination?.totalItems || 0})
            </h2>
            <div className="flex items-center gap-2">

              {/* Detection Type multi-select */}
              <div className="relative" ref={typeDropdownRef}>
                <button
                  onClick={() => { setTypeDropdownOpen(!typeDropdownOpen); setPlacementDropdownOpen(false) }}
                  className="inline-flex items-center px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-lg transition-colors shadow-sm bg-white hover:bg-gray-50"
                >
                  {selectedTypes.length === 0
                    ? 'All Types'
                    : `${selectedTypes.length} type${selectedTypes.length > 1 ? 's' : ''}`}
                  <ChevronDown className="h-3 w-3 ml-1.5 text-gray-400" />
                </button>
                {typeDropdownOpen && (
                  <div className="absolute top-full right-0 mt-1 z-50 w-52 bg-white rounded-lg shadow-xl border border-gray-200 py-1">
                    {selectedTypes.length > 0 && (
                      <button
                        onClick={() => { setSelectedTypes([]); setPostsPage(1) }}
                        className="w-full text-left px-3 py-1.5 text-xs text-blue-600 hover:bg-blue-50 transition-colors border-b border-gray-100"
                      >
                        Clear selection
                      </button>
                    )}
                    {[
                      { value: 'caption_mention', label: 'Caption Mention' },
                      { value: 'hashtag', label: 'Hashtag' },
                      { value: 'visual_logo', label: 'Visual Logo' },
                      { value: 'visual_product', label: 'Visual Product' },
                      { value: 'visual_text_overlay', label: 'Visual Text Overlay' },
                    ].map(({ value, label }) => (
                      <button
                        key={value}
                        onClick={() => {
                          setSelectedTypes(prev =>
                            prev.includes(value) ? prev.filter(t => t !== value) : [...prev, value]
                          )
                          setPostsPage(1)
                        }}
                        className="w-full flex items-center px-3 py-1.5 text-xs hover:bg-gray-50 transition-colors"
                      >
                        <div className={`h-3.5 w-3.5 mr-2 rounded border flex items-center justify-center flex-shrink-0 ${
                          selectedTypes.includes(value) ? 'bg-blue-600 border-blue-600' : 'border-gray-300'
                        }`}>
                          {selectedTypes.includes(value) && <Check className="h-2.5 w-2.5 text-white" />}
                        </div>
                        <span className="text-gray-700">{label}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Placement Tier multi-select */}
              <div className="relative" ref={placementDropdownRef}>
                <button
                  onClick={() => { setPlacementDropdownOpen(!placementDropdownOpen); setTypeDropdownOpen(false) }}
                  className="inline-flex items-center px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-lg transition-colors shadow-sm bg-white hover:bg-gray-50"
                >
                  {selectedPlacements.length === 0
                    ? 'All Placements'
                    : `${selectedPlacements.length} placement${selectedPlacements.length > 1 ? 's' : ''}`}
                  <ChevronDown className="h-3 w-3 ml-1.5 text-gray-400" />
                </button>
                {placementDropdownOpen && (
                  <div className="absolute top-full right-0 mt-1 z-50 w-52 bg-white rounded-lg shadow-xl border border-gray-200 py-1">
                    {selectedPlacements.length > 0 && (
                      <button
                        onClick={() => { setSelectedPlacements([]); setPostsPage(1) }}
                        className="w-full text-left px-3 py-1.5 text-xs text-blue-600 hover:bg-blue-50 transition-colors border-b border-gray-100"
                      >
                        Clear selection
                      </button>
                    )}
                    {[
                      { value: 'background', label: 'Background' },
                      { value: 'primary', label: 'Primary' },
                      { value: 'product_placement', label: 'Product Placement' },
                      { value: 'secondary', label: 'Secondary' },
                      { value: 'text_mention', label: 'Text Mention' },
                    ].map(({ value, label }) => (
                      <button
                        key={value}
                        onClick={() => {
                          setSelectedPlacements(prev =>
                            prev.includes(value) ? prev.filter(t => t !== value) : [...prev, value]
                          )
                          setPostsPage(1)
                        }}
                        className="w-full flex items-center px-3 py-1.5 text-xs hover:bg-gray-50 transition-colors"
                      >
                        <div className={`h-3.5 w-3.5 mr-2 rounded border flex items-center justify-center flex-shrink-0 ${
                          selectedPlacements.includes(value) ? 'bg-blue-600 border-blue-600' : 'border-gray-300'
                        }`}>
                          {selectedPlacements.includes(value) && <Check className="h-2.5 w-2.5 text-white" />}
                        </div>
                        <span className="text-gray-700">{label}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Content Category multi-select (alphabetized; empty = all categories) */}
              <div className="relative" ref={categoryDropdownRef}>
                <button
                  onClick={() => { setCategoryDropdownOpen(!categoryDropdownOpen); setTypeDropdownOpen(false); setPlacementDropdownOpen(false) }}
                  className="inline-flex items-center px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-lg transition-colors shadow-sm bg-white hover:bg-gray-50"
                >
                  {selectedCategories.length === 0
                    ? 'All Categories'
                    : `${selectedCategories.length} categor${selectedCategories.length > 1 ? 'ies' : 'y'}`}
                  <ChevronDown className="h-3 w-3 ml-1.5 text-gray-400" />
                </button>
                {categoryDropdownOpen && (
                  <div className="absolute top-full right-0 mt-1 z-50 w-52 max-h-72 overflow-y-auto bg-white rounded-lg shadow-xl border border-gray-200 py-1">
                    {selectedCategories.length > 0 && (
                      <button
                        onClick={() => { setSelectedCategories([]); setPostsPage(1) }}
                        className="w-full text-left px-3 py-1.5 text-xs text-blue-600 hover:bg-blue-50 transition-colors border-b border-gray-100"
                      >
                        Clear selection
                      </button>
                    )}
                    {[
                      { value: 'behind_the_scenes', label: 'Behind The Scenes' },
                      { value: 'community_charity', label: 'Community Charity' },
                      { value: 'fan_engagement', label: 'Fan Engagement' },
                      { value: 'final_result_recap', label: 'Final Result Recap' },
                      { value: 'game_day_announcement', label: 'Game Day Announcement' },
                      { value: 'highlight', label: 'Highlight' },
                      { value: 'hype_promo', label: 'Hype Promo' },
                      { value: 'in_game_update', label: 'In Game Update' },
                      { value: 'news_announcement', label: 'News Announcement' },
                      { value: 'other', label: 'Other' },
                      { value: 'player_spotlight', label: 'Player Spotlight' },
                      { value: 'throwback', label: 'Throwback' },
                      { value: 'ticketing_merch', label: 'Ticketing Merch' },
                      { value: 'training_practice', label: 'Training Practice' },
                    ].map(({ value, label }) => (
                      <button
                        key={value}
                        onClick={() => {
                          setSelectedCategories(prev =>
                            prev.includes(value) ? prev.filter(t => t !== value) : [...prev, value]
                          )
                          setPostsPage(1)
                        }}
                        className="w-full flex items-center px-3 py-1.5 text-xs hover:bg-gray-50 transition-colors"
                      >
                        <div className={`h-3.5 w-3.5 mr-2 rounded border flex items-center justify-center flex-shrink-0 ${
                          selectedCategories.includes(value) ? 'bg-blue-600 border-blue-600' : 'border-gray-300'
                        }`}>
                          {selectedCategories.includes(value) && <Check className="h-2.5 w-2.5 text-white" />}
                        </div>
                        <span className="text-gray-700">{label}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

            </div>
          </div>

          {postsLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
          ) : brandPosts.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 flex flex-col items-center justify-center py-12 text-center">
              <div className="h-12 w-12 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                <Radar className="h-6 w-6 text-gray-400" />
              </div>
              <h3 className="text-sm font-semibold text-gray-900 mb-1">No posts found</h3>
              <p className="text-xs text-gray-500">No posts with this brand have been detected yet</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {brandPosts.map((post) => (
                  <ScoutPostCard key={post.post_id} post={post} customerId={customerId} />
                ))}
              </div>

              {/* Pagination — matches the standard on the Brands page */}
              {postsPagination && postsPagination.totalItems > 0 && (
                <div className="flex items-center justify-between border-t border-gray-200 px-3 py-4 mt-4">
                  <div className="text-xs text-gray-500">
                    Showing {(postsPagination.currentPage - 1) * 20 + 1}-{Math.min(postsPagination.currentPage * 20, postsPagination.totalItems)} of {postsPagination.totalItems} posts
                  </div>
                  {postsPagination.totalPages > 1 && (
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setPostsPage(1)}
                        disabled={!postsPagination.hasPrevPage || postsLoading}
                        className="inline-flex items-center px-3 py-1.5 text-xs font-medium border rounded-lg transition-colors shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                      >
                        First
                      </button>
                      <button
                        onClick={() => setPostsPage((p) => Math.max(1, p - 1))}
                        disabled={!postsPagination.hasPrevPage || postsLoading}
                        className="inline-flex items-center px-3 py-1.5 text-xs font-medium border rounded-lg transition-colors shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                      >
                        Previous
                      </button>
                      <span className="text-xs text-gray-600">
                        Page {postsPagination.currentPage} of {postsPagination.totalPages}
                      </span>
                      <button
                        onClick={() => setPostsPage((p) => p + 1)}
                        disabled={!postsPagination.hasNextPage || postsLoading}
                        className="inline-flex items-center px-3 py-1.5 text-xs font-medium border rounded-lg transition-colors shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                      >
                        Next
                      </button>
                      <button
                        onClick={() => setPostsPage(postsPagination.totalPages)}
                        disabled={!postsPagination.hasNextPage || postsLoading}
                        className="inline-flex items-center px-3 py-1.5 text-xs font-medium border rounded-lg transition-colors shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                      >
                        Last
                      </button>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default BrandDetailPage
